import React, { useState } from "react";
import tw from "twin.macro";
import styled from "styled-components";
import useAnimatedNavToggler from "../../helpers/useAnimatedNavToggler.js";

import logo from "../../images/mai-logo.png";
import { ReactComponent as MenuIcon } from "feather-icons/dist/icons/menu.svg";
import { ReactComponent as CloseIcon } from "feather-icons/dist/icons/x.svg";
import { ReactComponent as ChevronDownIcon } from "feather-icons/dist/icons/chevron-down.svg";

const Header = tw.header`
  flex justify-between items-center
  max-w-screen-xl mx-auto
`;

export const NavLinks = tw.div`inline-block`;

/* hocus: stands for "on hover or focus"
 * hocus:bg-primary-700 will apply the bg-primary-700 class on hover or focus
 */
export const NavLink = tw.a`
  text-lg my-2 lg:text-base lg:mx-6 lg:my-0
  font-semibold tracking-wide transition duration-300
  pb-1 border-b-2 border-transparent hover:border-primary-500 hocus:text-primary-500
`;

export const DropdownContainer = tw.div`relative inline-block w-full lg:w-auto`;

export const DropdownButton = tw.button`
  text-lg my-2 lg:text-base lg:mx-6 lg:my-0
  font-semibold tracking-wide transition duration-300
  pb-1 border-b-2 border-transparent hover:border-primary-500 hocus:text-primary-500
  flex items-center justify-center lg:justify-start focus:outline-none w-full lg:w-auto
`;

export const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 0.5rem;
  width: 12rem;
  background-color: white;
  border-radius: 0.5rem;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  z-index: 50;
  opacity: ${props => props.isOpen ? '1' : '0'};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transition: all 0.2s;
  
  @media (max-width: 1023px) {
    position: relative;
    top: 0;
    left: 0;
    margin-top: 0;
    width: 100%;
    background-color: transparent;
    box-shadow: none;
    border: 0;
    opacity: ${props => props.isOpen ? '1' : '0'};
    visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
    max-height: ${props => props.isOpen ? '200px' : '0'};
    overflow: hidden;
  }
`;

export const DropdownItem = styled.a`
  display: block;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  color: #374151;
  transition: all 0.2s;
  
  &:hover {
    background-color: #f3f4f6;
    color: var(--secondary-500, #c7942d);
  }
  
  &:first-child {
    border-top-left-radius: 0.5rem;
    border-top-right-radius: 0.5rem;
  }
  
  &:last-child {
    border-bottom-left-radius: 0.5rem;
    border-bottom-right-radius: 0.5rem;
  }
  
  @media (min-width: 1024px) {
    font-size: 1rem;
    padding: 0.5rem 1rem;
  }
  
  @media (max-width: 1023px) {
    font-size: 1.125rem;
    padding: 0.5rem 0;
    background-color: transparent;
    border-bottom: 1px solid #e5e7eb;
    
    &:hover {
      background-color: transparent;
    }
    
    &:last-child {
      border-bottom: 0;
    }
  }
`;

export const PrimaryLink = tw(NavLink)`
  lg:mx-0
  px-8 py-3 rounded bg-primary-500 text-gray-100
  hocus:bg-primary-700 hocus:text-gray-200 focus:shadow-outline
  border-b-0
`;

export const LogoLink = styled(NavLink)`
  ${tw`flex items-center font-black border-b-0 flex-row text-2xl! ml-0!`};
  img {
    ${tw`w-12 mr-3`}
  }
`;

export const MobileNavLinksContainer = tw.nav`lg:hidden flex flex-1 items-center justify-between`;
export const NavToggle = tw.button`
  lg:hidden z-20 focus:outline-none hocus:text-primary-500 transition duration-300
`;
export const MobileNavLinks = styled.div((props) => [
  tw`fixed top-0 inset-x-0 mx-4 my-6 p-8 border text-center rounded-lg text-gray-900 bg-white`,
  `display: ${props.showLinks ? "block" : "none"};z-index: 1111`,
]);
export const StyledNavLinks = styled.div`
  ${NavLinks} {
    ${tw`flex flex-col items-center`}
  }
`;

export const DesktopNavLinks = tw.nav`
  hidden lg:flex flex-1 justify-between items-center
`;

export default ({
  roundedHeaderButton = false,
  logoLink,
  links,
  className,
  collapseBreakpointClass = "lg",
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { showNavLinks, animation, toggleNavbar } = useAnimatedNavToggler();
  
  const defaultLinks = [
    <NavLinks key={1}>
      <NavLink href="/about-us" onClick={toggleNavbar}>About</NavLink>
      <NavLink href="/pricing" onClick={toggleNavbar}>Pricing</NavLink>
      <NavLink href="/use-cases" onClick={toggleNavbar}>Use Cases</NavLink>
      <NavLink href="/conferences" onClick={toggleNavbar}>Conferences</NavLink>
      <DropdownContainer>
        <DropdownButton
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          onMouseEnter={() => window.innerWidth >= 1024 && setIsDropdownOpen(true)}
          onMouseLeave={() => window.innerWidth >= 1024 && setIsDropdownOpen(false)}
        >
          Research
          <ChevronDownIcon tw="w-4 h-4 ml-1" />
        </DropdownButton>
        <DropdownMenu 
          isOpen={isDropdownOpen}
          onMouseEnter={() => window.innerWidth >= 1024 && setIsDropdownOpen(true)}
          onMouseLeave={() => window.innerWidth >= 1024 && setIsDropdownOpen(false)}
        >
          <DropdownItem href="/research#home" onClick={toggleNavbar}>Ongoing Research</DropdownItem>
          <DropdownItem href="/research#upcoming-study" onClick={toggleNavbar}>Upcoming Study</DropdownItem>
          <DropdownItem href="/research#research-papers" onClick={toggleNavbar}>Research Papers</DropdownItem>
        </DropdownMenu>
      </DropdownContainer>
      <NavLink href="/mrimai" onClick={toggleNavbar}>MRI</NavLink>
      <NavLink href="https://app.maimotion.com/login" onClick={toggleNavbar}>Login</NavLink>
      <PrimaryLink
        css={roundedHeaderButton && tw`rounded-full`}
        href="https://app.maimotion.com/register"
        onClick={toggleNavbar}
      >
        <span style={{ whiteSpace: "nowrap" }}>Sign Up</span>
      </PrimaryLink>
    </NavLinks>,
  ];
  const collapseBreakpointCss =
    collapseBreakPointCssMap[collapseBreakpointClass];

  const defaultLogoLink = (
    <LogoLink href="/">
      <img src={logo} alt="logo" />
      MAI Motion
    </LogoLink>
  );

  logoLink = logoLink || defaultLogoLink;
  links = links || defaultLinks;

  return (
    <Header className={className || "header-light"}>
      <DesktopNavLinks css={collapseBreakpointCss.desktopNavLinks}>
        {logoLink}
        {links}
      </DesktopNavLinks>

      <MobileNavLinksContainer
        css={collapseBreakpointCss.mobileNavLinksContainer}
      >
        {logoLink}
        <MobileNavLinks
          showLinks={showNavLinks}
          initial={{ x: "150%" }}
          animate={animation}
          css={collapseBreakpointCss.mobileNavLinks}
        >
          <StyledNavLinks>{links}</StyledNavLinks>
        </MobileNavLinks>
        <NavToggle
          onClick={toggleNavbar}
          className={showNavLinks ? "open" : "closed"}
        >
          {showNavLinks ? (
            <CloseIcon tw="w-6 h-6" />
          ) : (
            <MenuIcon tw="w-6 h-6" />
          )}
        </NavToggle>
      </MobileNavLinksContainer>
    </Header>
  );
};

/* The below code is for generating dynamic break points for navbar.
 * Using this you can specify if you want to switch
 * to the toggleable mobile navbar at "sm", "md" or "lg" or "xl" above using the collapseBreakpointClass prop
 * Its written like this because we are using macros and we can not insert dynamic variables in macros
 */

const collapseBreakPointCssMap = {
  sm: {
    mobileNavLinks: tw`sm:hidden`,
    desktopNavLinks: tw`sm:flex`,
    mobileNavLinksContainer: tw`sm:hidden`,
  },
  md: {
    mobileNavLinks: tw`md:hidden`,
    desktopNavLinks: tw`md:flex`,
    mobileNavLinksContainer: tw`md:hidden`,
  },
  lg: {
    mobileNavLinks: tw`lg:hidden`,
    desktopNavLinks: tw`lg:flex`,
    mobileNavLinksContainer: tw`lg:hidden`,
  },
  xl: {
    mobileNavLinks: tw`lg:hidden`,
    desktopNavLinks: tw`lg:flex`,
    mobileNavLinksContainer: tw`lg:hidden`,
  },
};
